import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const TimeOffRequestsContext = createContext();

export const TimeOffRequestsProvider = ({ children }) => {
  const [timeOffRequests, setTimeOffRequests] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
  });

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    include: "user,policy,approvals,delegate",
  });

  const handleSearchChange = (value) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      search: value,
      currentPage: 1, // Reset to first page when searching
    }));
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      handleSearchChange(e.target.value);
    }
  };

  const fetchTimeOffRequests = useCallback(async () => {
    setTimeOffRequests((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/ahr/time-off-requests${queryString}`);
      if (response.success) {
        setTimeOffRequests({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination.last_page,
        }));
      }
    } catch (error) {
      setTimeOffRequests({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  useEffect(() => {
    fetchTimeOffRequests();
  }, [fetchTimeOffRequests]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = timeOffRequests.pagination?.last_page || 1;

  const PaginationComponent = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  const Pagination = () => PaginationComponent;

  return (
    <TimeOffRequestsContext.Provider
      value={{
        timeOffRequests,
        filterOptions,
        handleSearch,
        handleSearchChange,
        refetch: fetchTimeOffRequests,
        Pagination,
      }}
    >
      {children}
    </TimeOffRequestsContext.Provider>
  );
};

export const useTimeOffRequests = () => {
  const context = useContext(TimeOffRequestsContext);
  if (!context) {
    throw new Error("useTimeOffRequests must be used within TimeOffRequestsProvider");
  }
  return context;
};
